import { Link } from 'expo-router';
import { useIntl } from 'react-intl';
import { GestureResponderEvent, Linking, StyleSheet, View } from 'react-native';

import {
  Button,
  DatetimeShow,
  IconCartFill,
  IconCatering,
  IconDelivery,
  ScrollView,
  YStack,
  YStackProps,
} from '@fhs/ui';
import Footer from '@fhs-legacy/frontend/src/components/footer';
import { useClickLogs } from '@fhs-legacy/frontend/src/hooks/use-click-logs';
import { region } from '@fhs-legacy/frontend/src/utils/environment';
import { isExternalLink } from '@fhs-legacy/frontend/src/utils/is-external-link';

import { hotSauceBarCanadaEndDate, hotSauceBarCanadaStartDate } from './constants';
import { HomeAlert } from './home-alert';
import { HomeCard } from './home-card';
import {
  CareersCard,
  CateringCard,
  HookAndLadderCard,
  HotSauceBarCard,
  PublicSafetyFoundationCard,
} from './marketing-cards-mobile';
import { HotSauceBarCanadaCard } from './marketing-cards-mobile/hot-sauce-bar-canada-card';
import { TopMarketingImage } from './top-marketing-image-mobile';
import { ScreenHomeProps } from './types';

type CtaButtonStackProps = {
  style?: YStackProps['style'];
  isDeliveryEnabled: boolean;
  isCateringEnabled: boolean;
  shouldShowDelivery: boolean;
  cateringRedirectPath: string;
  pickUpRedirectPath: string;
  deliveryRedirectPath: string;
  onDeliverySelect: (event: GestureResponderEvent) => void;
};

function CtaButtonStack(props: CtaButtonStackProps) {
  const { formatMessage } = useIntl();
  const { logPickUpOrderClickEvent, logDeliveryOrderClickEvent, logCateringOrderClickEvent } =
    useClickLogs();
  const pickUpOrderText = formatMessage({ id: 'pickUpOrder' });
  const deliveryOrderText = formatMessage({ id: 'deliveryOrder' });

  return (
    <YStack style={styles.ctaButtonStack}>
      <Link href={props.pickUpRedirectPath} asChild>
        <Button
          type="solid"
          size="lg"
          testID="homepage-pickup-order"
          onPress={logPickUpOrderClickEvent}
        >
          <Button.Icon>
            <IconCartFill />
          </Button.Icon>
          <Button.Text>{pickUpOrderText}</Button.Text>
        </Button>
      </Link>

      {props.shouldShowDelivery && (
        <Link href={props.deliveryRedirectPath} asChild>
          <Button
            type="outline"
            size="lg"
            disabled={!props.isDeliveryEnabled}
            onPress={props.onDeliverySelect}
            onPressOut={logDeliveryOrderClickEvent}
          >
            <Button.Icon>
              <IconDelivery />
            </Button.Icon>
            <Button.Text>{deliveryOrderText}</Button.Text>
          </Button>
        </Link>
      )}

      <Link
        asChild
        onPressIn={logCateringOrderClickEvent}
        {...(isExternalLink(props.cateringRedirectPath)
          ? {
              href: '',
              onPressOut: () => {
                Linking.openURL(props.cateringRedirectPath);
              },
            }
          : { href: props.cateringRedirectPath })}
      >
        <Button type="outline" size="lg" disabled={!props.isCateringEnabled}>
          <Button.Icon>
            <IconCatering />
          </Button.Icon>
          <Button.Text>Catering Order</Button.Text>
        </Button>
      </Link>
    </YStack>
  );
}

function MarketingCards({
  cateringHref,
  pickUpHref,
  careersHref,
  foundationHref,
}: {
  cateringHref: string;
  pickUpHref: string;
  foundationHref: string;
  careersHref: string;
  donateHref: string;
}) {
  return (
    <YStack style={styles.verticalCardStack}>
      {region() === 'US' ? (
        <HotSauceBarCard />
      ) : (
        <DatetimeShow
          after={hotSauceBarCanadaStartDate}
          before={hotSauceBarCanadaEndDate}
          default={<PublicSafetyFoundationCard foundationHref={foundationHref} />}
        >
          <HotSauceBarCanadaCard />
        </DatetimeShow>
      )}
      <CareersCard careersHref={careersHref} />
      <HookAndLadderCard pickUpHref={pickUpHref} />
      {Boolean(cateringHref) && <CateringCard cateringHref={cateringHref} />}
    </YStack>
  );
}

export function ScreenHomeMobile(props: ScreenHomeProps) {
  return (
    <ScrollView
      contentContainerStyle={styles.scrollViewContent}
      showsVerticalScrollIndicator={false}
    >
      <div
        style={{
          backgroundColor: 'white',
          backgroundImage: `url(${require('./brick-wall-white.webp').uri})`,
          backgroundRepeat: 'repeat',
          backgroundSize: '750px auto',
          minHeight: '100%',
        }}
      >
        {props.alert && props.alert.text && (
          <HomeAlert text={props.alert.text} link={props.alert.link} />
        )}
        {props.userPointExpireAlert && props.userPointExpireAlert.text && (
          <HomeAlert
            text={props.userPointExpireAlert.text}
            link={props.userPointExpireAlert.link}
          />
        )}
        <View style={styles.contentContainer}>
          <View style={styles.topContent}>
            <TopMarketingImage style={styles.topMarketingImage} />
            <View style={styles.homeCardContainer}>
              <HomeCard
                logReorderCardPress={props.logReorderCardPress}
                displayedOrder={props.displayedOrder}
                displayedOrderCardLoading={props.displayedOrderCardLoading}
                redirectPath={props.redirectPath}
                onReorder={props.onReorder}
                reorderLoading={props.reorderLoading}
                challengesData={props.challengesData}
                isLoadingChallenges={props.isLoadingChallenges}
                loyaltyUserReady={props.loyaltyUserReady}
                isDesktop={props.isDesktop}
                onPressChallenge={props.onPressChallenge}
                showChallengeInfo={props.showChallengeInfo}
              />
            </View>

            <CtaButtonStack
              isCateringEnabled={props.isCateringEnabled}
              isDeliveryEnabled={props.isDeliveryEnabled}
              shouldShowDelivery={props.shouldShowDelivery}
              cateringRedirectPath={props.cateringRedirectPath}
              pickUpRedirectPath={props.pickUpRedirectPath}
              deliveryRedirectPath={props.deliveryRedirectPath}
              onDeliverySelect={props.onDeliverySelect}
            />
          </View>
          <MarketingCards
            cateringHref={props.cateringRedirectPath}
            pickUpHref={props.pickUpRedirectPath}
            foundationHref={props.foundationPath}
            careersHref={props.careersPath}
            donateHref={props.donatePath}
          />
        </View>
        <Footer />
      </div>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  contentContainer: {
    maxWidth: 480,
    width: '100%',
    gap: 20,
    paddingTop: 48,
    marginHorizontal: 'auto',
    alignItems: 'center',
  },
  topContent: {
    width: '100%',
    alignItems: 'center',
    paddingHorizontal: 16,
  },

  topMarketingImage: {
    maxWidth: '60%',
    marginVertical: -20,
  },
  displayOrderContainer: {
    width: '100%',
  },
  ctaButtonStack: {
    gap: 8,
    width: '100%',
    marginTop: 20,
  },
  verticalCardStack: {
    width: '100%',
  },
  homeCardContainer: {
    alignSelf: 'stretch',
    marginHorizontal: -16,
    marginTop: 20,
  },
  scrollViewContent: {
    flexGrow: 1,
  },
});
